import { useContext } from 'react'

import { Button, GroupButton, SelectField, Text, TwistIcons, UserContext } from '@doist/marketist'

import cn from 'classnames'
import { useTranslation } from 'next-i18next'
import { loginLink } from 'services/loginLink'
import { currencyDetails, SupportedCurrencies } from 'services/pricing/price-table'

import css from './Controls.module.css'

export type BillingCycles = 'yearly' | 'monthly'

type PlanControlsProps = {
    children: React.ReactNode
    className?: string
}

export function PlanControls({ children, className }: PlanControlsProps) {
    return <div className={cn(css.controlsContainer, className)}>{children}</div>
}

type SignUpButtonProps = {
    divider?: boolean
}

PlanControls.SignUpButton = function SignUpButton({ divider }: SignUpButtonProps) {
    const { i18n, t } = useTranslation(['pricing', 'common'])
    const { isAuthenticated } = useContext(UserContext)

    return (
        <div className={cn({ [css.divider]: divider })}>
            <Button size="large" href={loginLink({ lang: i18n.language })}>
                {isAuthenticated ? t('menu.login', { ns: 'common' }) : t('signUpNow')}
            </Button>
        </div>
    )
}

type BillingCycleProps = {
    billingCycle?: BillingCycles
    onBillingCycleChange: (value: BillingCycles) => void
}

PlanControls.BillingCycle = function BillingCycle({
    billingCycle,
    onBillingCycleChange,
}: BillingCycleProps) {
    const { t } = useTranslation('pricing')

    return (
        <div>
            <Text tag="span" size="minor-base" className={css.cycle}>
                {t('cycle')}
            </Text>
            <GroupButton
                buttons={[
                    { name: t('yearly'), active: billingCycle === 'yearly', value: 'yearly' },
                    {
                        name: t('monthly'),
                        active: billingCycle === 'monthly',
                        value: 'monthly',
                    },
                ]}
                onClick={(value) => {
                    onBillingCycleChange(value as BillingCycles)
                }}
            />
        </div>
    )
}

type CurrencyProps = {
    currency: SupportedCurrencies
    onChange: (value: SupportedCurrencies) => void
}

PlanControls.Currency = function Currency({ currency, onChange }: CurrencyProps) {
    const { t } = useTranslation('pricing')

    return (
        <div>
            <SelectField
                id="currency"
                size="sm"
                icon={<TwistIcons.Globe />}
                label={t('changeCurrency')}
                value={currency}
                onChange={(e) => onChange(e.currentTarget.value as SupportedCurrencies)}
            >
                {Object.keys(currencyDetails).map((key) => (
                    <option value={key} key={key}>
                        {key.toUpperCase()}
                    </option>
                ))}
            </SelectField>
        </div>
    )
}
